// src/layout/AppShell.jsx
import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { NAV } from "../nav/navConfig";
import { useAuth } from "auth";

// se o seu BottomTabs exporta default, mantenha assim.
// se exporta named, troque para: import { BottomTabs } from "./BottomTabs";
import BottomTabs from "./BottomTabs";

function findArea(pathname) {
  return NAV.find((a) => {
    if (pathname === a.base || pathname.startsWith(a.base + "/")) return true;
    return (a.aliases || []).some((p) => pathname === p || pathname.startsWith(p + "/"));
  });
}

function initials(nameOrEmail) {
  const s = String(nameOrEmail || "").trim();
  if (!s) return "U";
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function AppShell() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { auth, logout } = useAuth();

  // macro-área atual (Sales / Data Flow / Performance / Support)
  const area = useMemo(() => findArea(pathname) || NAV[0], [pathname]);

  // persiste última rota
  useEffect(() => {
    if (pathname?.startsWith("/")) localStorage.setItem("last_route", pathname);
  }, [pathname]);

  // ===== Mobile drawer (sidebar) =====
  const [navOpen, setNavOpen] = useState(false);

  // fecha drawer ao navegar
  useEffect(() => {
    setNavOpen(false);
  }, [pathname]);

  // fecha drawer com ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setNavOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // ===== menu conta =====
  const [acctOpen, setAcctOpen] = useState(false);
  useEffect(() => {
    const onDown = (e) => {
      if (e.target?.closest?.(".accountMenuWrap")) return;
      setAcctOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const displayName = useMemo(() => {
    const name = String(auth?.name || "").trim();
    if (name) return name;
    return String(auth?.email || "Conta").trim();
  }, [auth?.name, auth?.email]);

  const badge = initials(auth?.name || auth?.email);

  const doLogout = async () => {
    setAcctOpen(false);
    try {
      await logout?.();
    } finally {
      navigate("/login");
    }
  };

  // ===== Tabs (submenus) movidos para BottomTabs =====
  const areaTabs = useMemo(() => {
    const tabs = (area?.tabs || []).map((t) => ({
      key: t.key,
      label: t.label,
      to: t.to,
      icon: t.icon
    }));
    return tabs;
  }, [area?.tabs]);



  const activeTabKey = useMemo(() => {
    // acha tab cujo `to` casa com a rota atual
    const t = (area?.tabs || []).find((x) => pathname === x.to || pathname.startsWith(x.to + "/"));
    return t?.key || area?.tabs?.[0]?.key || "";
  }, [area?.tabs, pathname]);

  const onTabChange = (key) => {
    const t = (area?.tabs || []).find((x) => x.key === key);
    if (t?.to) navigate(t.to);
  };

  return (
    <div className="appShell">
      <header className="topbar">
        <div className="topbarLeft">
          {/* botão menu (mobile) */}
          <button
            type="button"
            className="btnGhost menuBtn"
            onClick={() => setNavOpen(true)}
            aria-label="Abrir menu"
            aria-haspopup="dialog"
            aria-expanded={navOpen ? "true" : "false"}
          >
            ☰
          </button>

          {/* Título da Área (sem “JDPlat” aqui) */}
          <div className="brandText">
            <div className="brandName">{area?.label || "—"}</div>
            {/* Se quiser um subtítulo contextual mais tarde, coloque aqui */}
            {/* <div className="brandSub">…</div> */}
          </div>
        </div>

        <div className="topbarRight">

          <div className="accountMenuWrap" style={{ position: "relative" }}>
            <button
              className="btnPrimary"
              type="button"
              onClick={() => setAcctOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={acctOpen ? "true" : "false"}
              style={{ display: "inline-flex", alignItems: "center", gap: 10 }}
            >
              <span
                className="userBadge"
                aria-hidden
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 999,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid rgba(255,255,255,.18)",
                  background: "rgba(255,255,255,.08)",
                  fontWeight: 800,
                  fontSize: 12,
                  letterSpacing: ".02em",
                }}
              >
                {badge}
              </span>
              <span
                className="userName"
                style={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {displayName}
              </span>
              <span aria-hidden style={{ opacity: 0.7 }}>
                ▾
              </span>
            </button>

            {acctOpen && (
              <div
                className="accountMenu"
                role="menu"
                style={{
                  position: "absolute",
                  right: 0,
                  top: "calc(100% + 10px)",
                  minWidth: 220,
                  borderRadius: 14,
                  border: "1px solid rgba(255,255,255,.14)",
                  background: "rgba(10,18,22,.96)",
                  boxShadow: "0 20px 60px rgba(0,0,0,.45)",
                  padding: 8,
                  zIndex: 9999,
                }}
              >
                <div style={{ padding: "10px 10px 8px" }}>
                  <div style={{ fontSize: 12, opacity: 0.75 }}>Conectado como</div>
                  <div style={{ fontSize: 13, fontWeight: 750, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {displayName}
                  </div>
                </div>

                <div style={{ height: 1, background: "rgba(255,255,255,.10)", margin: "6px 0" }} />

                <button
                  role="menuitem"
                  type="button"
                  onClick={doLogout}
                  className="menuItem"
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "10px 10px",
                    borderRadius: 10,
                    background: "transparent",
                    border: "1px solid transparent",
                    color: "inherit",
                    cursor: "pointer",
                  }}
                >
                  Sair
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="shellBody">
        {/* backdrop do drawer */}
        {navOpen ? <div className="backdrop" onClick={() => setNavOpen(false)} /> : null}

        <aside className={"sidebar" + (navOpen ? " open" : "")} aria-label="Navegação">
          <div className="sidebarGroupTitle">Areas</div>
          <nav className="sidebarNav">
            {NAV.map((a) => (
              <NavLink key={a.key} to={a.defaultTo} className={({ isActive }) => "sideItem" + (isActive ? " active" : "")}>
                {a.label}
              </NavLink>
            ))}
          </nav>

          {/* <div style={{ height: 1, background: "rgba(255,255,255,.10)", margin: "14px 0" }} /> */}

          {/* <NavLink to="/account" className={({ isActive }) => "sideItem" + (isActive ? " active" : "")} style={{ opacity: 0.95 }}>
            Conta
          </NavLink> */}
        </aside>

        <main className="main">
          {/* Remove os tabs do header (agora são BottomTabs) */}
          <div className="content">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Submenus por área no bottom (só se tiver tabs) */}
      {areaTabs.length ? (
        <BottomTabs
          items={areaTabs}
          value={activeTabKey}
          onChange={onTabChange}
        />
      ) : null}
    </div>
  );
}
